import { format, isValid, parseISO, formatDistanceToNow } from 'date-fns';

export interface StatusConfig {
  label: string;
  color: 'default' | 'primary' | 'secondary' | 'info' | 'warning' | 'success' | 'error';
  step: number;
  needsAttention: boolean;
}

const STATUS_MAP: Record<string, StatusConfig> = {
  uploaded:          { label: 'Uploaded',      color: 'default',   step: 0, needsAttention: false },
  pending:           { label: 'Queued',        color: 'default',   step: 0, needsAttention: false },
  profiling:         { label: 'Analyzing',     color: 'info',      step: 1, needsAttention: false },
  profiled:          { label: 'Analyzed',      color: 'info',      step: 1, needsAttention: false },
  summarizing:       { label: 'Analyzing',     color: 'info',      step: 1, needsAttention: false },
  summarized:        { label: 'Analyzed',      color: 'info',      step: 1, needsAttention: false },
  ready_for_mapping: { label: 'Ready to Map',  color: 'info',      step: 2, needsAttention: false },
  mapping:           { label: 'Mapping in Progress', color: 'secondary', step: 2, needsAttention: false },
  mapped:            { label: 'Mapping Ready', color: 'warning',   step: 3, needsAttention: true },
  reviewing:         { label: 'In Review',     color: 'warning',   step: 3, needsAttention: true },
  approved:          { label: 'Approved',      color: 'success',   step: 4, needsAttention: false },
  transforming:      { label: 'Transforming',  color: 'info',      step: 5, needsAttention: false },
  loading:           { label: 'Transforming',  color: 'info',      step: 5, needsAttention: false },
  completed:         { label: 'Complete',      color: 'success',   step: 6, needsAttention: false },
  failed:            { label: 'Failed',        color: 'error',     step: -1, needsAttention: true },
  error:             { label: 'Error',         color: 'error',     step: -1, needsAttention: true },
};

const DEFAULT_STATUS: StatusConfig = {
  label: 'Unknown',
  color: 'default',
  step: 0,
  needsAttention: false,
};

export const TOTAL_STEPS = 7;

const UI_STATUS_MAP: Record<string, string> = {
  uploaded: 'pending',
  profiling: 'profiling',
  summarized: 'ready_for_mapping',
  mapping: 'mapping',
  mapped: 'mapped',
  approved: 'approved',
  transforming: 'transforming',
  loading: 'loading',
  completed: 'completed',
  failed: 'failed',
};

export const STEP_LABELS = [
  'Uploaded',
  'Analyzing',
  'Mapping',
  'Review',
  'Approved',
  'Transforming',
  'Complete',
];

export function getStatusConfig(status: string): StatusConfig {
  return STATUS_MAP[status] ?? DEFAULT_STATUS;
}

export function normalizeStatusForUi(status?: string | null): string {
  if (!status) return 'pending';
  return UI_STATUS_MAP[status] ?? status;
}

export function formatDate(
  value?: string | Date | null,
  pattern: string = 'MMM dd, yyyy'
): string {
  if (!value) return '-';

  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string') {
    const normalised = value.includes('T') ? value : value.replace(' ', 'T');
    date = parseISO(normalised);
    if (!isValid(date)) {
      date = new Date(normalised);
    }
  } else {
    return '-';
  }

  if (!isValid(date)) return '-';
  return format(date, pattern);
}

export function formatRelativeTime(value?: string | Date | null): string {
  if (!value) return '';

  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string') {
    date = new Date(value);
  } else {
    return '';
  }

  if (!isValid(date)) return '';
  return formatDistanceToNow(date, { addSuffix: true });
}

export function humanizeSchemaName(name: string): string {
  return name
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
